import { Link } from "react-router-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LaunchOfferCard } from "@/components/LaunchOffer";
import { PublicRoadmap, RoadmapNavLink } from "@/components/PublicRoadmap";
import { VideoShowcase } from "@/components/VideoShowcase";
import {
  Sparkles, ArrowRight, ClipboardList, Timer, Users, Layers,
  CalendarClock, CreditCard, ShieldCheck, WifiOff, CheckCircle2, Phone, Loader2, PlayCircle,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1580256081112-e49377338b7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEATURES = [
  { icon: ClipboardList, title: "Daily housekeeping board", body: "Turn each day's room list into assigned, trackable jobs. Departing and Due-Priority rooms get full cleans, in-house rooms a 15-minute service clean — imported from CSV or set by hand." },
  { icon: Timer, title: "Timesheets & payroll estimates", body: "Track clock-in/out, paid breaks and unpaid lunch, and per-room minutes. Export timesheets and estimate pay for hourly, salary or contract cleaners." },
  { icon: Users, title: "Multi-worker cleaning", body: "Several cleaners can assist on one room in independent sessions. Completed sub-tasks credit whoever actually did them, with partial release to share the load." },
  { icon: Layers, title: "Room stripping step", body: "Full cleans require the room to be stripped first. Cleaners earn a payroll credit for stripping; managers can strip as record-only." },
  { icon: CalendarClock, title: "Scheduled & recurring tasks", body: "Create one-off or recurring maintenance and checklist tasks for all, one, or some managers, with shared completion and Yours/All filtering." },
  { icon: CreditCard, title: "Simple weekly billing", body: "Start with a free trial, then subscribe per cleaner and per room. Prices lock when you convert, and time-limited discounts show exactly when they expire." },
  { icon: ShieldCheck, title: "Roles & per-property login", body: "Cleaner and Manager/Owner roles keep the right people on the right screens. Each property gets its own unique Property ID to log in, with data kept separate across sites." },
  { icon: WifiOff, title: "Works offline (PWA)", body: "Install KlenaPro on any phone or tablet. Cleaning keeps working on the floor even when the Wi-Fi drops, then syncs when it's back." },
];

const STEPS = [
  { n: "01", title: "Import the day", body: "Upload your daily room-status CSV or set statuses manually. KlenaPro decides which rooms need a full clean, a quick service, or nothing." },
  { n: "02", title: "Cleaners work the board", body: "Staff strip, start, and tick off rooms from their phone. Timers, breaks and lunch are tracked automatically." },
  { n: "03", title: "Managers see the truth", body: "Live productivity, accurate per-room time, and payroll-ready timesheets — no spreadsheets, no guesswork." },
];

const FAQS = [
  { q: "What is KlenaPro?", a: "KlenaPro is housekeeping operations software for motels, motor inns, hotels and BnBs. It manages daily room cleaning, cleaner timesheets, payroll estimates and cleaning productivity in one place." },
  { q: "How does pricing work?", a: "Every property gets a free trial, then a subscription priced per cleaner and per room, charged monthly. Only active cleaners and rooms count (plus any disabled ones that still had work in the last 30 days). Your rate locks when your trial converts to a paid plan." },
  { q: "Which languages does KlenaPro support?", a: "English, Punjabi, Filipino, Spanish, French, Portuguese, Polish, German and Arabic. Every user picks their own language, and team messages are automatically translated between languages." },
  { q: "Does it handle timesheets and payroll?", a: "Yes. Clock-in, breaks, lunch and per-room time become weekly timesheets that cleaners sign and managers authorise, exported as PDF, CSV or XLSX with pay-period locking and public holidays." },
  { q: "Do cleaners need training?", a: "No. Cleaners open the app on their phone, strip and start a room, and tick items as they go. It works offline and syncs automatically." },
  { q: "Can it handle multiple properties?", a: "Yes. KlenaPro is multi-tenant and each property gets its own unique Property ID to log in, so pricing, cleaners and data stay separate for every site." },
];

export default function Landing() {
  const { startDemo } = useAuth();
  const navigate = useNavigate();
  const [demoBusy, setDemoBusy] = useState(false);

  const launchDemo = async () => {
    setDemoBusy(true);
    try {
      await startDemo();
      navigate("/manager");
    } catch {
      toast.error("Could not start the demo. Please try again.");
      setDemoBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-lg font-extrabold leading-none">KlenaPro</div>
              <div className="label-caps text-muted-foreground">Housekeeping Ops</div>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
            <a href="#videos" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-videos-link">Videos</a>
            <a href="#features" className="text-muted-foreground transition-colors hover:text-foreground">Features</a>
            <a href="#how" className="text-muted-foreground transition-colors hover:text-foreground">How it works</a>
            <RoadmapNavLink className="text-muted-foreground transition-colors hover:text-foreground" />
            <a href="#faq" className="text-muted-foreground transition-colors hover:text-foreground">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="k-press gap-1.5" onClick={launchDemo} disabled={demoBusy} data-testid="nav-demo-btn">
              {demoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Try live demo
            </Button>
            <Link to="/login" data-testid="nav-signin-btn">
              <Button variant="ghost" size="sm" className="k-press">Sign in</Button>
            </Link>
            <Link to="/signup" data-testid="nav-trial-btn">
              <Button size="sm" className="k-press gap-1.5">Start free trial <ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <div className="k-fade-in">
            <div className="label-caps mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Built for motels &amp; BnBs
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              Faster cleans.<br />Fairer timesheets.<br />
              <span className="text-white/60">Zero guesswork.</span>
            </h1>
            <p className="mt-6 max-w-md text-base text-white/70 sm:text-lg">
              KlenaPro turns your daily room list into assigned, trackable cleaning jobs —
              with live productivity, accurate per-room time, and payroll-ready timesheets.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" variant="secondary" className="k-press gap-2" onClick={launchDemo} disabled={demoBusy} data-testid="hero-demo-btn">
                {demoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-5 w-5" />} Try live demo — no signup
              </Button>
              <Link to="/signup" data-testid="hero-trial-btn">
                <Button size="lg" variant="ghost" className="k-press gap-2 border border-white/20 text-white hover:bg-white/10 hover:text-white">
                  Start your free trial <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-signin-btn">
                <Button size="lg" variant="ghost" className="k-press text-white hover:bg-white/10 hover:text-white">
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
              {["Free trial", "No card to start", "Works offline"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-xl border border-white/10 shadow-2xl">
              <img src={HERO_IMG} alt="Housekeeping cart in a motel corridor ready for daily room cleaning" className="h-[420px] w-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <VideoShowcase />

      {/* Launch offer */}
      <section id="launch-offer" className="mx-auto max-w-6xl px-6 pt-16">
        <LaunchOfferCard publicMode />
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-6 py-20">
        <div className="max-w-2xl">
          <div className="label-caps text-muted-foreground">Everything in one place</div>
          <h2 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">Run housekeeping without the spreadsheets</h2>
          <p className="mt-3 text-base text-muted-foreground">From the first stripped bed to the final payroll export, KlenaPro tracks the work as it actually happens.</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="group rounded-lg border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl">
            <div className="label-caps text-muted-foreground">How it works</div>
            <h2 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">Three steps from checkout to clean</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-lg border border-border bg-card p-6">
                <div className="font-display text-4xl font-extrabold text-muted-foreground/30">{s.n}</div>
                <h3 className="mt-3 font-display text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="rounded-xl border border-border bg-card p-8 sm:p-12">
          <div className="label-caps text-muted-foreground">Who it's for</div>
          <h2 className="mt-2 max-w-3xl font-display text-3xl font-extrabold sm:text-4xl">Made for motels, motor inns, hotels and BnBs</h2>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            If you have rooms to turn over every day and a cleaning team to pay fairly, KlenaPro
            replaces the whiteboard and the WhatsApp group with one accurate, real-time system.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "Owners who want fair, defensible timesheets",
              "Managers who need the daily board at a glance",
              "Cleaners who just want a clear, tap-through list",
              "Multi-property operators managing every site",
              "Teams sharing rooms across busy turnover days",
              "Anyone tired of paper run-sheets and guesswork",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2 text-sm text-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {t}
              </div>
            ))}
          </div>
        </div>
      </section>
      <PublicRoadmap />


      {/* FAQ */}
      <section id="faq" className="border-t border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Frequently asked questions</h2>
          <div className="mt-8 divide-y divide-border">
            {FAQS.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="font-display text-lg font-bold">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-6 py-16 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Ready to clean smarter?</h2>
            <p className="mt-2 text-white/70">Start your free trial today — set up your rooms in minutes.</p>
          </div>
          <Link to="/signup" data-testid="cta-trial-btn">
            <Button size="lg" variant="secondary" className="k-press gap-2">
              Start free trial <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display font-extrabold">KlenaPro</span>
            <span className="text-sm text-muted-foreground">— Housekeeping management</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <a href="tel:+64274524519" className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground" data-testid="footer-phone">
              <Phone className="h-4 w-4" /> +64 027 452 4519
            </a>
            <Link to="/terms" className="transition-colors hover:text-foreground" data-testid="footer-terms">Terms &amp; Conditions</Link>
            <Link to="/login" className="transition-colors hover:text-foreground">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
